import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Property } from '../types';
import { MapPin } from 'lucide-react';


interface PropertyMapProps {
  properties: Property[];
  selectedProperty?: Property | null;
  onPropertySelect?: (property: Property) => void;
  className?: string;
}

const PropertyMap: React.FC<PropertyMapProps> = ({
  properties,
  selectedProperty,
  onPropertySelect,
  className = "w-full h-96"
}) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const markers = useRef<{ [key: string]: mapboxgl.Marker }>({});
  const [mapboxToken, setMapboxToken] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');

  // Fetch Mapbox token from Supabase secrets
  useEffect(() => {
    const fetchMapboxToken = async () => {
      try {
        const response = await fetch('https://ngagtienwooguwquvtbn.supabase.co/functions/v1/get-mapbox-token', {
          method: 'GET',
          headers: {
            'Authorization': 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im5nYWd0aWVud29vZ3V3cXV2dGJuIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTI5Nzg5MDEsImV4cCI6MjA2ODU1NDkwMX0.SemTUlcrkgdevxKKAYvobk3hf7j5K7rF1dH-fdxhCCA'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setMapboxToken(data.token);
          console.log('Mapbox token loaded successfully');
        } else {
          console.error('Failed to fetch Mapbox token:', response.status);
          setError('Failed to load Mapbox configuration');
        }
      } catch (err) {
        console.error('Error fetching Mapbox token:', err);
        setError('Failed to load map configuration');
      } finally {
        setIsLoading(false);
      }
    };

    fetchMapboxToken();
  }, []);

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current || !mapboxToken || map.current) return;

    mapboxgl.accessToken = mapboxToken;
    
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: [-47.0608, -22.9068], // Campinas-SP, Brazil (fallback)
      zoom: 10,
      pitch: 0, // Set to 0 for 2D/flat view
      bearing: 0, // Remove any rotation
      projection: 'mercator' // Use mercator projection for flat 2D view
    });

    // Add navigation controls
    map.current.addControl(
      new mapboxgl.NavigationControl({
        visualizePitch: true,
      }),
      'top-right'
    );

    // Add fullscreen control
    map.current.addControl(new mapboxgl.FullscreenControl(), 'top-right');

    return () => {
      map.current?.remove();
      map.current = null;
    };
  }, [mapboxToken]);

  // Geocode properties and add markers
  useEffect(() => {
    if (!map.current || !properties.length) {
      console.log('PropertyMap: Missing requirements', { 
        hasMap: !!map.current, 
        propertiesCount: properties.length 
      });
      return;
    }

    console.log('PropertyMap: Starting to geocode and add markers for', properties.length, 'properties');

    const addMarkerToMap = (property: Property, lng: number, lat: number) => {
      if (!map.current) {
        console.log('PropertyMap: No map available for adding marker');
        return;
      }

      console.log('PropertyMap: Adding marker for property', property.propertyCode, 'at', lng, lat);

      // Get primary image URL for this property
      const primaryImageUrl = property.media.images[0]?.url;

      // Create custom marker element
      const markerEl = document.createElement('div');
      markerEl.className = 'custom-marker';
      markerEl.innerHTML = `
        <div class="bg-orange-500 text-white rounded-full w-10 h-10 flex items-center justify-center shadow-lg cursor-pointer hover:bg-orange-600 transition-all duration-200 hover:scale-110 relative group">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
          </svg>
          <div class="absolute -top-1 -right-1 w-3 h-3 bg-white rounded-full border-2 border-orange-500"></div>
        </div>
      `;

      // Create hover popup
      const hoverPopup = new mapboxgl.Popup({
        offset: 25,
        closeButton: false,
        closeOnClick: false,
        className: 'property-hover-popup'
      }).setHTML(`
        <div class="p-3 max-w-xs bg-white rounded-lg shadow-xl border-0">
          <div class="relative mb-3">
            <img src="${primaryImageUrl}" 
                 alt="${property.title}" 
                 class="w-full h-32 object-cover rounded-lg" />
            <div class="absolute top-2 left-2">
              <span class="bg-orange-500 text-white text-xs px-2 py-1 rounded-full font-medium">
                ${property.transactionType}
              </span>
            </div>
          </div>
          <h3 class="font-semibold text-sm mb-2 line-clamp-2 text-gray-800">${property.title}</h3>
          <div class="flex items-center text-xs text-gray-600 mb-2">
            <svg class="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
            </svg>
            <span>${property.location.neighborhood}, ${property.location.city}</span>
          </div>
          <div class="flex items-center justify-between text-xs text-gray-600 mb-3">
            <div class="flex items-center space-x-3">
              <span>${property.bedrooms}q</span>
              <span>•</span>
              <span>${property.garage.quantity}v</span>
              <span>•</span>
              <span>${property.livingArea}m²</span>
            </div>
          </div>
          <div class="flex items-center justify-between">
            <span class="font-bold text-orange-600">${new Intl.NumberFormat('pt-BR', {
              style: 'currency',
              currency: 'BRL',
              minimumFractionDigits: 0
            }).format(property.listPrice)}</span>
            <button onclick="window.open('/imovel/${property.listingId}', '_blank')" 
                    class="text-xs bg-orange-500 text-white px-3 py-1.5 rounded-md hover:bg-orange-600 transition-colors font-medium">
              Ver Detalhes
            </button>
          </div>
        </div>
      `);

      // Create marker
      const marker = new mapboxgl.Marker(markerEl)
        .setLngLat([lng, lat])
        .addTo(map.current);

      // Add hover events with delay
      let hoverTimeout: NodeJS.Timeout;
      
      markerEl.addEventListener('mouseenter', () => {
        clearTimeout(hoverTimeout);
        marker.setPopup(hoverPopup);
        marker.togglePopup();
        
        // Add event listeners to popup when it opens
        setTimeout(() => {
          const popupElement = document.querySelector('.property-hover-popup .mapboxgl-popup-content');
          if (popupElement) {
            popupElement.addEventListener('mouseenter', () => {
              clearTimeout(hoverTimeout);
            });
            
            popupElement.addEventListener('mouseleave', () => {
              hoverTimeout = setTimeout(() => {
                const popup = marker.getPopup();
                if (popup === hoverPopup && popup && popup.isOpen()) {
                  marker.togglePopup();
                }
              }, 300);
            });
          }
        }, 100);
      });

      markerEl.addEventListener('mouseleave', () => {
        hoverTimeout = setTimeout(() => {
          const popup = marker.getPopup();
          if (popup === hoverPopup && popup && popup.isOpen()) {
            marker.togglePopup();
          }
        }, 300);
      });

      // Add click handler
      markerEl.addEventListener('click', () => {
        if (onPropertySelect) {
          onPropertySelect(property);
        }
        // Opcional: abrir página de detalhes
        // window.open(`/imovel/${property.listingId}`, '_blank');
      });

      markers.current[property.listingId] = marker;
      console.log('PropertyMap: Marker added successfully for property', property.propertyCode);
    };

    const geocodeAndAddMarkers = async () => {
      // Clear existing markers
      Object.values(markers.current).forEach(marker => marker.remove());
      markers.current = {};

      const bounds = new mapboxgl.LngLatBounds();
      let markerCount = 0;

      for (const property of properties) {
        try {
          console.log('PropertyMap: Processing property', property.propertyCode, property.location);
          
          // Check if property already has coordinates
          if (property.location.latitude && property.location.longitude) {
            console.log('PropertyMap: Using existing coordinates for', property.propertyCode);
            addMarkerToMap(property, property.location.longitude, property.location.latitude);
            bounds.extend([property.location.longitude, property.location.latitude]);
            markerCount++;
          } else {
            // Geocode the address
            const fullAddress = `${property.location.address}, ${property.location.neighborhood}, ${property.location.city}, ${property.location.state}, Brasil`;
            console.log('PropertyMap: Geocoding address:', fullAddress);
            
            const response = await fetch('https://ngagtienwooguwquvtbn.supabase.co/functions/v1/geocode-address', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im5nYWd0aWVud29vZ3V3cXV2dGJuIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTI5Nzg5MDEsImV4cCI6MjA2ODU1NDkwMX0.SemTUlcrkgdevxKKAYvobk3hf7j5K7rF1dH-fdxhCCA'
              },
              body: JSON.stringify({ address: fullAddress })
            });

            if (response.ok) {
              const data = await response.json();
              console.log('PropertyMap: Geocoding successful for', property.propertyCode, data);
              addMarkerToMap(property, data.longitude, data.latitude);
              bounds.extend([data.longitude, data.latitude]);
              markerCount++;
            } else {
              console.error('PropertyMap: Geocoding failed for', property.propertyCode, response.status);
            }
          }
        } catch (error) {
          console.error(`PropertyMap: Error geocoding property ${property.propertyCode}:`, error);
        }
      }

      console.log('PropertyMap: Total markers added:', markerCount);

      // Fit map to show all markers
      if (markerCount > 0) {
        // Wait a bit for markers to be fully added to the map
        setTimeout(() => {
          if (markerCount === 1) {
            // If only one marker, just center on it
            const firstMarker = Object.values(markers.current)[0];
            if (firstMarker) {
              console.log('PropertyMap: Centering on single marker');
              map.current?.flyTo({
                center: firstMarker.getLngLat(),
                zoom: 15,
                duration: 1000
              });
            }
          } else {
            // Fit to bounds with padding for multiple markers
            console.log('PropertyMap: Fitting bounds for', markerCount, 'markers');
            map.current?.fitBounds(bounds, { 
              padding: 100,
              maxZoom: 15,
              duration: 1000
            });
          }
        }, 500);
      } else {
        console.log('PropertyMap: No markers were added to the map');
      }
    };

    geocodeAndAddMarkers();
  }, [properties, onPropertySelect]);

  // Highlight selected property
  useEffect(() => {
    if (!selectedProperty || !markers.current[selectedProperty.listingId]) return;

    const marker = markers.current[selectedProperty.listingId];
    
    // Center on selected property
    map.current?.flyTo({
      center: marker.getLngLat(),
      zoom: 16,
      duration: 1000
    });

    // Show popup
    marker.togglePopup();
  }, [selectedProperty]);

  if (isLoading) {
    return (
      <div className={`${className} bg-gray-100 rounded-lg flex items-center justify-center`}>
        <div className="flex items-center space-x-2 text-gray-600">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500"></div>
          <span>Carregando mapa...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${className} bg-gray-100 rounded-lg flex items-center justify-center`}>
        <div className="text-center text-gray-600">
          <MapPin className="w-8 h-8 mx-auto mb-2" />
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${className} relative rounded-lg overflow-hidden shadow-lg`}>
      <div ref={mapContainer} className="w-full h-full" />
      
      {/* Map legend */}
      <div className="absolute bottom-4 left-4 bg-white rounded-lg shadow-lg p-3">
        <div className="flex items-center space-x-2 text-sm">
          <div className="bg-orange-500 text-white rounded-full w-6 h-6 flex items-center justify-center">
            <MapPin className="w-3 h-3" />
          </div>
          <span className="text-gray-700">Imóveis disponíveis</span>
        </div>
      </div>
    </div>
  );
};

export default PropertyMap;